// =============================================================================
// MeuDriver — Admin Financial Overview
// Revenue, instructor payouts, monthly chart, transaction table
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate } from '@/lib/utils'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { BookingWithDetails } from '@/types/database'
import { TrendingUp, Wallet, Clock, Download, ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { PLATFORM_FEE_PERCENT } from '@/lib/constants'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MonthlyStat {
  label: string   // e.g. "Nov/25"
  revenue: number
  payout: number
}

interface FinancialSummary {
  totalRevenue: number
  totalPayouts: number
  pendingPayouts: number
  completedCount: number
  avgTicket: number
  thisMonthRevenue: number
  lastMonthRevenue: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(date: Date): string {
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  return `${months[date.getMonth()]}/${String(date.getFullYear()).slice(2)}`
}

// Aula que gerou cobrança = crédito consumido (concluída, ausência ou
// cancelamento tardio — ver trigger bookings_settle_credit). Aulas antigas,
// de antes da carteira, não têm `settlement`: vale o status concluída.
// Antes a página somava também cancelamentos com devolução como receita.
const CHARGED_FILTER = 'settlement.eq.consumed,and(settlement.is.null,status.eq.completed)'

function chargeReasonLabel(status: string): string {
  const labels: Record<string, string> = {
    completed: 'Aula dada',
    no_show: 'Ausência do aluno',
    cancelled_by_student: 'Cancelamento tardio',
  }
  return labels[status] ?? status
}

// ---------------------------------------------------------------------------
// SVG Bar Chart (simple, no external library)
// ---------------------------------------------------------------------------

interface BarChartProps {
  data: MonthlyStat[]
}

function BarChart({ data }: BarChartProps) {
  const CHART_H = 140
  const BAR_W = 28
  const GAP = 16
  const maxVal = Math.max(...data.flatMap((d) => [d.revenue, d.payout]), 1)

  const totalW = data.length * (BAR_W * 2 + GAP + 12) + 20

  return (
    <div className="overflow-x-auto">
      <svg
        width={totalW}
        height={CHART_H + 40}
        className="block"
        aria-label="Gráfico de receita e repasse mensal"
      >
        {data.map((stat, i) => {
          const x = 10 + i * (BAR_W * 2 + GAP + 12)
          const revenueH = Math.round((stat.revenue / maxVal) * CHART_H)
          const payoutH = Math.round((stat.payout / maxVal) * CHART_H)

          return (
            <g key={stat.label}>
              {/* Revenue bar */}
              <rect
                x={x}
                y={CHART_H - revenueH}
                width={BAR_W}
                height={revenueH}
                rx={3}
                className="fill-primary opacity-80"
              />
              {/* Payout bar */}
              <rect
                x={x + BAR_W + 4}
                y={CHART_H - payoutH}
                width={BAR_W}
                height={payoutH}
                rx={3}
                className="fill-emerald-400 opacity-80"
              />
              {/* Month label */}
              <text
                x={x + BAR_W + 2}
                y={CHART_H + 16}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px]"
                fontSize={10}
              >
                {stat.label}
              </text>
            </g>
          )
        })}
        {/* Baseline */}
        <line
          x1={0}
          y1={CHART_H}
          x2={totalW}
          y2={CHART_H}
          stroke="currentColor"
          strokeOpacity={0.15}
          strokeWidth={1}
        />
      </svg>
      {/* Legend */}
      <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary opacity-80" />
          Receita da plataforma
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-400 opacity-80" />
          Repasse aos instrutores
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CSV Export
// ---------------------------------------------------------------------------

function exportCsv(bookings: BookingWithDetails[]) {
  const header = ['Data', 'Aluno', 'Instrutor', 'Valor Total', 'Taxa Plataforma', 'Repasse Instrutor', 'Motivo']
  const rows = bookings.map((b) => [
    formatDate(b.scheduled_at, 'dd/MM/yyyy'),
    b.student?.full_name ?? '',
    b.instructor?.profile?.full_name ?? '',
    b.total_price ?? 0,
    b.platform_fee ?? 0,
    b.instructor_payout ?? 0,
    chargeReasonLabel(b.status),
  ])

  const csvContent = [header, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(','))
    .join('\n')

  const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `financeiro-meudriver-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Instructor Drill-Down Modal
// ---------------------------------------------------------------------------

interface InstructorStats {
  instructorId: string
  name: string
  avatarUrl?: string
  totalLessons: number
  totalRevenue: number
  totalPayout: number
  platformFee: number
}

function buildInstructorStats(bookings: BookingWithDetails[]): InstructorStats[] {
  const map: Record<string, InstructorStats> = {}
  for (const b of bookings) {
    if (!b.instructor_id) continue
    const id = b.instructor_id
    if (!map[id]) {
      map[id] = {
        instructorId: id,
        name: b.instructor?.profile?.full_name ?? 'Instrutor',
        avatarUrl: b.instructor?.profile?.avatar_url ?? undefined,
        totalLessons: 0,
        totalRevenue: 0,
        totalPayout: 0,
        platformFee: 0,
      }
    }
    map[id].totalLessons++
    map[id].totalRevenue += b.total_price ?? 0
    map[id].totalPayout += b.instructor_payout ?? 0
    map[id].platformFee += b.platform_fee ?? 0
  }
  return Object.values(map).sort((a, b) => b.totalPayout - a.totalPayout)
}

function getInitialsLocal(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((n) => n[0].toUpperCase()).join('')
}

interface DrillDownModalProps {
  stats: InstructorStats
  bookings: BookingWithDetails[]
  onClose: () => void
}

function InstructorDrillDownModal({ stats, bookings, onClose }: DrillDownModalProps) {
  const instructorBookings = bookings
    .filter((b) => b.instructor_id === stats.instructorId)
    .sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at))

  // Monthly breakdown
  const monthlyMap: Record<string, { revenue: number; payout: number; count: number }> = {}
  for (const b of instructorBookings) {
    const key = b.scheduled_at.slice(0, 7)
    if (!monthlyMap[key]) monthlyMap[key] = { revenue: 0, payout: 0, count: 0 }
    monthlyMap[key].revenue += b.total_price ?? 0
    monthlyMap[key].payout += b.instructor_payout ?? 0
    monthlyMap[key].count++
  }
  const months = Object.entries(monthlyMap).slice(0, 6).map(([key, v]) => ({ key, ...v }))

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={stats.avatarUrl} />
              <AvatarFallback className="text-xs font-semibold bg-brand-100 text-brand-700">
                {getInitialsLocal(stats.name)}
              </AvatarFallback>
            </Avatar>
            {stats.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl bg-gray-50 border p-3">
              <p className="text-xl font-bold text-gray-900">{stats.totalLessons}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Aulas</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-xl font-bold text-emerald-700">{formatCurrency(stats.totalPayout)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Repasse</p>
            </div>
            <div className="rounded-xl bg-brand-50 border border-brand-200 p-3">
              <p className="text-xl font-bold text-brand-700">{formatCurrency(stats.platformFee)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Taxa plataforma</p>
            </div>
          </div>

          {/* Monthly breakdown */}
          {months.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Histórico mensal</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {months.map(({ key, revenue, payout, count }) => (
                  <div key={key} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-gray-50 border">
                    <span className="text-muted-foreground font-mono text-xs">{key}</span>
                    <span className="text-xs text-muted-foreground">{count} aula(s)</span>
                    <span className="text-emerald-700 font-medium">{formatCurrency(payout)}</span>
                    <span className="text-brand-600 font-medium">{formatCurrency(revenue - payout)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent transactions */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Transações recentes</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {instructorBookings.slice(0, 10).map((b) => (
                <div key={b.id} className="flex items-center justify-between text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50">
                  <span className="text-muted-foreground">{formatDate(b.scheduled_at)}</span>
                  <span className="text-gray-700">{b.student?.full_name ?? '—'}</span>
                  <span className="font-medium text-emerald-700">
                    {b.instructor_payout != null ? formatCurrency(b.instructor_payout) : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AdminFinancial() {
  const [summary, setSummary] = useState<FinancialSummary>({
    totalRevenue: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    completedCount: 0,
    avgTicket: 0,
    thisMonthRevenue: 0,
    lastMonthRevenue: 0,
  })
  const [summaryLoading, setSummaryLoading] = useState(true)

  const [monthlyStats, setMonthlyStats] = useState<MonthlyStat[]>([])
  const [chartLoading, setChartLoading] = useState(true)

  const [transactions, setTransactions] = useState<BookingWithDetails[]>([])
  const [txLoading, setTxLoading] = useState(true)

  const [drillDownInstructor, setDrillDownInstructor] = useState<InstructorStats | null>(null)

  // ── Fetch summary ───────────────────────────────────────────────────────────

  async function fetchSummary() {
    setSummaryLoading(true)
    try {
      const { data: completedData, error } = await supabase
        .from('bookings')
        .select('platform_fee, instructor_payout, total_price, scheduled_at, status')
        .or(CHARGED_FILTER)

      if (error) throw error

      const completed = completedData ?? []
      const totalRevenue = completed.reduce((s, b) => s + (b.platform_fee ?? 0), 0)
      const totalPayouts = completed.reduce((s, b) => s + (b.instructor_payout ?? 0), 0)
      const completedCount = completed.length
      const avgTicket = completedCount > 0
        ? completed.reduce((s, b) => s + (b.total_price ?? 0), 0) / completedCount
        : 0

      // Month-over-month revenue (platform_fee)
      const now = new Date()
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString()
      const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString()
      const thisMonthRevenue = completed
        .filter((b) => (b as any).scheduled_at >= thisMonthStart)
        .reduce((s, b) => s + (b.platform_fee ?? 0), 0)
      const lastMonthRevenue = completed
        .filter((b) => (b as any).scheduled_at >= lastMonthStart && (b as any).scheduled_at <= lastMonthEnd)
        .reduce((s, b) => s + (b.platform_fee ?? 0), 0)

      // Pending = confirmed bookings not yet completed
      const { data: pendingData, error: pendingErr } = await supabase
        .from('bookings')
        .select('instructor_payout')
        .eq('status', 'confirmed')

      if (pendingErr) throw pendingErr

      const pendingPayouts = (pendingData ?? []).reduce(
        (s, b) => s + (b.instructor_payout ?? 0),
        0,
      )

      setSummary({ totalRevenue, totalPayouts, pendingPayouts, completedCount, avgTicket, thisMonthRevenue, lastMonthRevenue })
    } catch {
      toast.error('Erro ao carregar resumo financeiro')
    } finally {
      setSummaryLoading(false)
    }
  }

  // ── Fetch monthly stats (last 6 months) ─────────────────────────────────────

  async function fetchMonthlyStats() {
    setChartLoading(true)
    try {
      const now = new Date()
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1)

      const { data, error } = await supabase
        .from('bookings')
        .select('scheduled_at, platform_fee, instructor_payout, status')
        .or(CHARGED_FILTER)
        .gte('scheduled_at', sixMonthsAgo.toISOString())
        .order('scheduled_at', { ascending: true })

      if (error) throw error

      // Build map for last 6 months
      const statsMap: Record<string, MonthlyStat> = {}
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const key = monthKey(d)
        statsMap[key] = { label: monthLabel(d), revenue: 0, payout: 0 }
      }

      for (const row of data ?? []) {
        const key = row.scheduled_at.slice(0, 7)
        if (statsMap[key]) {
          statsMap[key].revenue += row.platform_fee ?? 0
          statsMap[key].payout += row.instructor_payout ?? 0
        }
      }

      setMonthlyStats(Object.values(statsMap))
    } catch {
      toast.error('Erro ao carregar gráfico mensal')
    } finally {
      setChartLoading(false)
    }
  }

  // ── Fetch transaction table ─────────────────────────────────────────────────

  async function fetchTransactions() {
    setTxLoading(true)
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(
          `*, student:profiles!bookings_student_id_fkey(*), instructor:instructors!bookings_instructor_id_fkey(*, profile:profiles!instructors_profile_id_fkey(*))`,
        )
        .or(CHARGED_FILTER)
        .order('scheduled_at', { ascending: false })
        .limit(50)

      if (error) throw error
      setTransactions((data as unknown as BookingWithDetails[]) ?? [])
    } catch {
      toast.error('Erro ao carregar transações')
    } finally {
      setTxLoading(false)
    }
  }

  useEffect(() => {
    fetchSummary()
    fetchMonthlyStats()
    fetchTransactions()
  }, [])

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Financeiro</h1>
          <p className="mt-1 text-muted-foreground">
            Receita da plataforma, repasses e histórico de transações.
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-2 self-start sm:self-auto"
          disabled={txLoading || transactions.length === 0}
          onClick={() => exportCsv(transactions)}
        >
          <Download className="h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {/* Total Revenue */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Receita Total da Plataforma
            </CardTitle>
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <p className="text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              +{PLATFORM_FEE_PERCENT}% sobre o preço dos instrutores (aulas concluídas)
            </p>
          </CardContent>
        </Card>

        {/* Total Instructor Payouts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Repasse Total aos Instrutores
            </CardTitle>
            <Wallet className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <p className="text-2xl font-bold">{formatCurrency(summary.totalPayouts)}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Preço cheio dos instrutores (aulas concluídas)</p>
          </CardContent>
        </Card>

        {/* Pending Payouts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Repasses Pendentes
            </CardTitle>
            <Clock className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(summary.pendingPayouts)}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Aulas confirmadas aguardando conclusão
            </p>
          </CardContent>
        </Card>

        {/* Ticket médio */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ticket Médio</CardTitle>
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-32" /> : (
              <p className="text-2xl font-bold">{formatCurrency(summary.avgTicket)}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              {summary.completedCount} aula(s) concluída(s)
            </p>
          </CardContent>
        </Card>

        {/* Variação mês a mês */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Mês Atual</CardTitle>
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-32" /> : (
              <>
                <p className="text-2xl font-bold">{formatCurrency(summary.thisMonthRevenue)}</p>
                {summary.lastMonthRevenue > 0 && (() => {
                  const pct = Math.round(((summary.thisMonthRevenue - summary.lastMonthRevenue) / summary.lastMonthRevenue) * 100)
                  const up = pct >= 0
                  return (
                    <p className={`mt-1 text-xs font-medium ${up ? 'text-green-600' : 'text-red-500'}`}>
                      {up ? '▲' : '▼'} {Math.abs(pct)}% vs. mês anterior
                    </p>
                  )
                })()}
                {summary.lastMonthRevenue === 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">Primeiro mês com dados</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Receita Mensal — Últimos 6 Meses</CardTitle>
        </CardHeader>
        <CardContent>
          {chartLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : monthlyStats.every((s) => s.revenue === 0 && s.payout === 0) ? (
            <p className="py-10 text-center text-muted-foreground">
              Nenhuma receita registrada nos últimos 6 meses.
            </p>
          ) : (
            <BarChart data={monthlyStats} />
          )}
        </CardContent>
      </Card>

      {/* Per-Instructor Breakdown */}
      {!txLoading && transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Por instrutor</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Instrutor</th>
                    <th className="px-4 py-3 font-medium text-center">Aulas</th>
                    <th className="px-4 py-3 font-medium text-right">Receita Total</th>
                    <th className="px-4 py-3 font-medium text-right">Repasse</th>
                    <th className="px-4 py-3 font-medium text-right">Taxa</th>
                    <th className="px-4 py-3 w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {buildInstructorStats(transactions).map((stats) => (
                    <tr
                      key={stats.instructorId}
                      className="hover:bg-muted/20 transition-colors cursor-pointer"
                      onClick={() => setDrillDownInstructor(stats)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarImage src={stats.avatarUrl} />
                            <AvatarFallback className="text-[10px] font-bold bg-brand-100 text-brand-700">
                              {getInitialsLocal(stats.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{stats.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-muted-foreground">{stats.totalLessons}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(stats.totalRevenue)}</td>
                      <td className="px-4 py-3 text-right text-emerald-700 font-medium">{formatCurrency(stats.totalPayout)}</td>
                      <td className="px-4 py-3 text-right text-brand-700">{formatCurrency(stats.platformFee)}</td>
                      <td className="px-4 py-3">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction Table — instructor names are clickable for drill-down */}
      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle>Aulas cobradas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {txLoading ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : transactions.length === 0 ? (
            <p className="py-12 text-center text-muted-foreground">
              Nenhuma aula cobrada ainda.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Data</th>
                    <th className="px-4 py-3 font-medium">Aluno</th>
                    <th className="px-4 py-3 font-medium">Instrutor</th>
                    <th className="px-4 py-3 font-medium text-right">Valor</th>
                    <th className="px-4 py-3 font-medium text-right">Taxa (+{PLATFORM_FEE_PERCENT}%)</th>
                    <th className="px-4 py-3 font-medium text-right">Repasse (instrutor)</th>
                    <th className="px-4 py-3 font-medium">Motivo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transactions.map((booking) => {
                    const instructorStats = buildInstructorStats(transactions).find(
                      (s) => s.instructorId === booking.instructor_id,
                    )
                    return (
                    <tr
                      key={booking.id}
                      className="hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {formatDate(booking.scheduled_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {booking.student?.full_name ?? '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          className="text-brand-600 hover:text-brand-800 hover:underline font-medium transition-colors"
                          onClick={() => instructorStats && setDrillDownInstructor(instructorStats)}
                        >
                          {booking.instructor?.profile?.full_name ?? '—'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                        {booking.total_price != null
                          ? formatCurrency(booking.total_price)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-green-700 whitespace-nowrap">
                        {booking.platform_fee != null
                          ? formatCurrency(booking.platform_fee)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-brand-700 whitespace-nowrap">
                        {booking.instructor_payout != null
                          ? formatCurrency(booking.instructor_payout)
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={booking.status === 'completed' ? 'secondary' : 'outline'}>
                          {chargeReasonLabel(booking.status)}
                        </Badge>
                      </td>
                    </tr>
                  )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {drillDownInstructor && (
        <InstructorDrillDownModal
          stats={drillDownInstructor}
          bookings={transactions}
          onClose={() => setDrillDownInstructor(null)}
        />
      )}
    </div>
  )
}
